import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import PropTypes from 'prop-types';
import { translate } from 'translations/i18n';

import { useModal } from 'components/shared/hooks';
import Steven, { ELEMENT_TYPES } from 'javascripts/helpers/steven';
import { canAccessFeature, FEATURES } from 'helpers/features';
import CCPopover from 'components/shared/DesignSystem/SlabTransferPortal/Floating/CCPopover';
import UpgradeIcon from 'components/shared/DesignSystem/UpgradeIcon';
import { Avatar, Button } from '@companycam/slab-web';
import { baseButtonStyles } from 'components/shared/DesignSystem/shared/styles';
import Spinner from 'components/shared/DesignSystem/Spinner';
import useTaskListAssignedUsers from 'components/projects/taskList/assign/hooks/useTaskListAssignedUsers';
import { getFullNameOrEmail } from './helpers';
import AssignContent from './AssignContent';

const AssignButton = styled.button`
  padding: 0 var(--cc_size_spacing_m);
`;

// #region styled-components
const avatarEnter = keyframes`
  to {
    opacity: 1;
    transform: scale(1) translate3d(0, 0, 0);
  }
`;

const Container = styled.div`
  margin-block-start: 12px;
`;

const TriggerButton = styled.button`
  ${baseButtonStyles}
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--cc_size_spacing_xs);
`;

const FauxIconButton = styled.span`
  display: grid;
  place-items: center;
  width: var(--cc_size_avatar_m);
  height: var(--cc_size_avatar_m);
  background: var(--cc_color_button_background_subtle);
  color: var(--cc_color_button_text_subtle);
  border-radius: 100%;
  transition: background-color 0.3s;
  transform: scale(0.5) translate3d(0, 0, 0);
  animation-name: ${avatarEnter};
  animation-delay: ${(props) => props.enterDelay}s;
  animation-duration: 0.5s;
  animation-timing-function: cubic-bezier(0.175, 0.885, 0.32, 1.275);
  animation-fill-mode: forwards;
  &:hover {
    background-color: var(--cc_color_button_background_subtle_darken_10);
  }
`;

const StyledCCPopover = styled(CCPopover)`
  z-index: 102; // reorder tasks header has a z-index of 101 :(
`;

// TODO: slabify this when Avatar takes extra props, see slab/#425
const StyledAvatar = styled(Avatar)`
  overflow: visible;
  opacity: 0;
  transform: scale(0.5) translate3d(0, 0, 0);
  animation-name: ${avatarEnter};
  animation-delay: ${(props) => props.enterDelay}s;
  animation-duration: 0.5s;
  animation-timing-function: cubic-bezier(0.175, 0.885, 0.32, 1.275);
  animation-fill-mode: forwards;
`;

const ContentContainer = styled.div`
  padding: var(--cc_size_spacing_m);
  width: 320px;
`;

const StyledUpgradeIcon = styled(UpgradeIcon)`
  margin-inline-start: var(--cc_size_spacing_s);
`;
// #endregion

const Assign = ({ taskListId }) => {
  const {
    assignedUsers,
    loading: isLoading,
    featureSet
  } = useTaskListAssignedUsers({
    taskListId,
  });
  const canAccessChecklistAssignment = canAccessFeature(
    featureSet,
    FEATURES.CHECKLIST_ASSIGNMENT,
  );
  const { showModal, Modal } = useModal();
  const [isAssignedUserPopoverOpen, setAssignedUserPopoverOpen] =
    useState(false);

  const hasAssignees = assignedUsers.length > 0;

  const handleToggle = () => {
    if (!isAssignedUserPopoverOpen) {
      Steven.elementClicked({
        id: 'checklist_assign',
        content: 'Assign Users',
        type: ELEMENT_TYPES.BUTTON,
        action: 'checklist_assign_opened',
      });
    }
    setAssignedUserPopoverOpen((prevIsOpen) => !prevIsOpen);
  };

  const renderAssignedUsers = (
    <>
      {assignedUsers.map(
        ({ avatar, id, firstName, lastName, emailAddress }, index) => {
          const fullAssignedUserName = getFullNameOrEmail({
            firstName,
            lastName,
            emailAddress,
          });
          return (
            <StyledAvatar
              key={id}
              fullName={fullAssignedUserName}
              imageSrc={avatar}
              className="hint hint--top-center"
              enterDelay={index * 0.02}
              aria-label={fullAssignedUserName}
            />
          );
        },
      )}
      <FauxIconButton
        aria-hidden="true"
        enterDelay={(assignedUsers.length + 2) * 0.02}
      >
        <i className="mdi mdi-account-plus" />
      </FauxIconButton>
    </>
  );

  if (isLoading) {
    return (
      <Container>
        <Spinner
          trackColor="var(--cc_color_background_1)"
          meterColor="var(--cc_color_border_input_inactive)"
          size="small"
        />
      </Container>
    );
  }

  // account can access Checklists but CANNOT access Checklist Assigning
  if (!canAccessChecklistAssignment) {
    return (
      <Container>
        {/* account may have assignments persisting if they downgraded, so show assigned users */}
        {hasAssignees ? (
          <TriggerButton
            onClick={() => showModal(Modal.UPGRADE_TASK_LIST_ASSIGN_MODAL)}
          >
            {renderAssignedUsers}
            <StyledUpgradeIcon size="medium" />
          </TriggerButton>
        ) : (
          <Button
            onClick={() => showModal(Modal.UPGRADE_TASK_LIST_ASSIGN_MODAL)}
            size="small"
            icon={{
              name: 'account-plus',
              position: 'before',
            }}
            data-testid="button__upgrade-task-list-assign"
          >
            {translate('projects.taskList.assign.assignUsersButton')}
            <StyledUpgradeIcon size="medium" />
          </Button>
        )}
      </Container>
    );
  }

  return (
    <Container>
      <StyledCCPopover
        placement="bottom-start"
        isOpen={isAssignedUserPopoverOpen}
        onOpenChange={handleToggle}
        trigger={
          hasAssignees ? (
            <TriggerButton type="button" aria-label="Open assign users menu">
              {renderAssignedUsers}
            </TriggerButton>
          ) : (
            <AssignButton
              data-testid="project__checklist__assign-user-button"
              className="ccb-light-gray-small"
            >
              <i className="mdi mdi-account-plus" aria-hidden="true" />
              {translate('projects.taskList.assign.assignUsersButton')}
            </AssignButton>
          )
        }
      >
        <ContentContainer>
          <AssignContent
            taskListId={taskListId}
            assignedUsers={assignedUsers}
            isEmpty={!hasAssignees}
          />
        </ContentContainer>
      </StyledCCPopover>
    </Container>
  );
};

Assign.propTypes = {
  taskListId: PropTypes.string.isRequired,
};

export default Assign;
